/**
 * Performance monitoring hooks for PostHog
 * Tracks performance metrics and slow operations
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { usePostHog } from './usePostHog';

/**
 * Hook to track component render performance
 * Use this to identify slow rendering components
 */
export const useRenderPerformanceTracking = (componentName: string, threshold = 16) => {
  const { captureEvent } = usePostHog();
  const renderStartTime = useRef<number>();

  useEffect(() => {
    // Start timing on mount/render
    renderStartTime.current = performance.now();

    // Use requestAnimationFrame to measure after DOM updates
    requestAnimationFrame(() => {
      if (renderStartTime.current) {
        const renderTime = performance.now() - renderStartTime.current;

        // Only track if render time exceeds threshold (default 16ms for 60fps)
        if (renderTime > threshold) {
          captureEvent('component_slow_render', {
            component_name: componentName,
            render_time_ms: Math.round(renderTime),
            threshold_ms: threshold,
            url: window.location.pathname,
          });
        }
      }
    });
  });
};

/**
 * Hook to track API call performance
 * Returns a function to wrap API calls and track their performance
 */
export const useApiPerformanceTracking = () => {
  const { captureEvent } = usePostHog();

  const trackApiCall = useCallback(
    async <T>(
      apiCall: () => Promise<T>,
      options: {
        operation: string;
        slowThreshold?: number; // ms
        errorThreshold?: number; // ms for timeout tracking
      }
    ): Promise<T> => {
      const { operation, slowThreshold = 1000, errorThreshold = 10000 } = options;
      const startTime = performance.now();

      try {
        const result = await apiCall();
        const duration = performance.now() - startTime;

        // Track slow API calls
        if (duration > slowThreshold) {
          captureEvent('api_slow_response', {
            operation,
            duration_ms: Math.round(duration),
            slow_threshold_ms: slowThreshold,
            url: window.location.pathname,
          });
        }

        // Track very slow API calls separately (potential timeouts)
        if (duration > errorThreshold) {
          captureEvent('api_timeout_risk', {
            operation,
            duration_ms: Math.round(duration),
            timeout_threshold_ms: errorThreshold,
            url: window.location.pathname,
          });
        }

        return result;
      } catch (error) {
        const duration = performance.now() - startTime;

        // Track API errors with timing
        captureEvent('api_error_with_timing', {
          operation,
          duration_ms: Math.round(duration),
          error_message: error instanceof Error ? error.message : 'Unknown error',
          url: window.location.pathname,
        });

        throw error;
      }
    },
    [captureEvent]
  );

  return { trackApiCall };
};

/**
 * Hook to track memory usage warnings
 */
export const useMemoryPerformanceTracking = () => {
  const { captureEvent } = usePostHog();

  useEffect(() => {
    let lastNotificationTime = 0;
    const notificationCooldown = 60000; // 1 minute cooldown between notifications

    // Check memory usage every 30 seconds if Performance API is available
    const checkMemory = () => {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        const usedPercent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
        const now = Date.now();

        // Track high memory usage with throttling to prevent spam
        if (usedPercent > 80 && now - lastNotificationTime > notificationCooldown) {
          captureEvent('high_memory_usage', {
            used_heap_mb: Math.round(memory.usedJSHeapSize / 1024 / 1024),
            total_heap_mb: Math.round(memory.totalJSHeapSize / 1024 / 1024),
            limit_heap_mb: Math.round(memory.jsHeapSizeLimit / 1024 / 1024),
            usage_percent: Math.round(usedPercent),
            url: window.location.pathname,
          });
          lastNotificationTime = now;
        }
      }
    };

    // Check immediately and then every 30 seconds
    checkMemory();
    const interval = setInterval(checkMemory, 30000);

    return () => clearInterval(interval);
  }, [captureEvent]);
};

/**
 * Hook to track bundle loading performance
 */
export const useBundlePerformanceTracking = () => {
  const { captureEvent } = usePostHog();

  useEffect(() => {
    // Track when page is fully loaded
    const trackPageLoadComplete = () => {
      // Use Navigation Timing API if available
      if ('performance' in window && 'getEntriesByType' in performance) {
        const navEntries = performance.getEntriesByType(
          'navigation'
        ) as PerformanceNavigationTiming[];

        if (navEntries.length > 0) {
          const nav = navEntries[0];
          const loadTime = nav.loadEventEnd - nav.fetchStart;
          const domContentLoaded = nav.domContentLoadedEventEnd - nav.fetchStart;
          const domInteractive = nav.domInteractive - nav.fetchStart;

          // Track page load metrics
          captureEvent('page_load_performance', {
            total_load_time_ms: Math.round(loadTime),
            dom_content_loaded_ms: Math.round(domContentLoaded),
            dom_interactive_ms: Math.round(domInteractive),
            dns_lookup_ms: Math.round(nav.domainLookupEnd - nav.domainLookupStart),
            tcp_connect_ms: Math.round(nav.connectEnd - nav.connectStart),
            request_response_ms: Math.round(nav.responseEnd - nav.requestStart),
            url: window.location.pathname,
          });

          // Track slow page loads
          if (loadTime > 3000) {
            captureEvent('slow_page_load', {
              load_time_ms: Math.round(loadTime),
              url: window.location.pathname,
            });
          }
        }
      }
    };

    // Run after page is fully loaded
    if (document.readyState === 'complete') {
      trackPageLoadComplete();
    } else {
      window.addEventListener('load', trackPageLoadComplete);
      return () => window.removeEventListener('load', trackPageLoadComplete);
    }
  }, [captureEvent]);
};

/**
 * High-order component to add performance tracking to any component
 */
export const withPerformanceTracking = function <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName: string,
  options?: { renderThreshold?: number }
) {
  const PerformanceTrackedComponent: React.FC<P> = (props) => {
    useRenderPerformanceTracking(componentName, options?.renderThreshold);
    return React.createElement(WrappedComponent, props);
  };

  PerformanceTrackedComponent.displayName = `withPerformanceTracking(${componentName})`;
  return PerformanceTrackedComponent;
};
